import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
    NavLink,
    useParams,
    useRouteMatch,
    useHistory,
} from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import _ from 'lodash';
import Typography from '../Typography/CustomTypography';
import AdminHeader from '../Header/AdminHeader';

import { LogoutRequested } from '../../actions/Auth';
import { GetDetailCompanyForSidebarRequested } from '../../actions/AdminCompanyManagement';

import { LogOutIcon, DashboardIcon, Company } from '../Icons/Icon';

import styles from '../../assets/jss/components/sidebarStyle';
import { Role } from '../../common/Constants';
import EventDialog from '../../containers/admin/EventManagement/EventDialog';
import SnackBar from '../Snackbar/Snackbar';

const useStyles = makeStyles(styles);

const SideBar = (props) => {
    const classes = useStyles();
    const {
        color,
        image,
        routes,
        open,
        handleDrawerToggle,
        logOut,
        eventInfo,
        organizerType,
        organizerLiveStream,
        fetchGetDetailCompanyForSideBar,
        organizerBooth,
        role,
        editSuccess,
    } = props;

    const match = useRouteMatch();
    const intl = useIntl();
    const params = useParams();
    const history = useHistory();
    const isCompanyDetailPage = window.location.pathname.indexOf('/company-detail') >= 0
    || window.location.pathname.indexOf('/seminar-management') >= 0
    || window.location.pathname.indexOf('/advertisement-management') >= 0
    || window.location.pathname.indexOf('/list-product-exhibition') >= 0
    || window.location.pathname.indexOf('/live-stream-management') >= 0
    || window.location.pathname.indexOf('/setting-product-company') >= 0;

    const [routeParams, setRouterParams] = useState('');
    const [filterRoutes, setFilterRoutes] = useState(routes);
    const [isOpenDialogEvent, setIsOpenDialogEvent] = useState(false);
    const [isOpenSnackBar, setIsOpenSnackBar] = useState({ isOpen: false, color: '', message: '' });

    useEffect(() => {
        const temp = Object.keys(params).map((key) => params[key]).join('/');
        setRouterParams(temp);
        if (params.shortName && params.idCompany) {
            fetchGetDetailCompanyForSideBar({
                event_name: params.shortName,
                company_id: params.idCompany,
            });
        }
    }, [params]);

    useEffect(() => {
        if (editSuccess) {
            setIsOpenSnackBar({ isOpen: true, color: 'success', message: intl.formatMessage({ id: 'apiSuccess.editEvent' }) });
        }
    }, [editSuccess]);

    useEffect(() => {
        if (!_.isEmpty(eventInfo)) {
            let filter = !eventInfo.outer_server_activation
                ? routes.filter((item) => item.path !== '/email-outer-server')
                : routes;

            if (!eventInfo.has_exhibition) {
                filter = filter.filter((item) => item.path !== '/list-product-exhibition');
            }

            if (!eventInfo.has_seminar) {
                filter = filter.filter((item) => item.path !== '/seminar-management');
            }

            if (!eventInfo.has_live_stream) {
                filter = filter.filter((item) => item.path !== '/live-stream-management');
            }

            if (!eventInfo.display_ads) {
                filter = filter.filter((item) => item.path !== '/advertisement-management');
            }

            if (organizerType === 0) {
                const ignoreAllRoutes = [
                    '/list-product-exhibition',
                    '/seminar-management',
                ];
                filter = filter.filter((route) => !ignoreAllRoutes.includes(route.path));
            }

            if (organizerType === 1) {
                const ignoreSeminarRoutes = [
                    '/seminar-management',
                ];
                filter = filter.filter((route) => !ignoreSeminarRoutes.includes(route.path));
            }

            if (organizerType === 2) {
                const ignoreExhibitionRoutes = [
                    '/list-product-exhibition',
                ];
                filter = filter.filter((route) => !ignoreExhibitionRoutes.includes(route.path));
            }

            if (!organizerLiveStream) {
                const liveStreamRoutes = [
                    '/live-stream-management',
                ];
                filter = filter.filter((route) => !liveStreamRoutes.includes(route.path));
            }

            if (!(eventInfo.has_shopping_cart)) {
                const shoppingCart = [
                    '/shopping-cart',
                ];
                filter = filter.filter((item) => !shoppingCart.includes(item.path));
            }

            if (!(eventInfo.has_shopping_cart && eventInfo.grand_zone)) {
                const shoppingCart = [
                    '/product-payment-management',
                ];
                filter = filter.filter((item) => !shoppingCart.includes(item.path));
            }
            if (!(eventInfo.has_shopping_cart && eventInfo.grand_zone && organizerBooth === 4)) {
                const shoppingCart = [
                    '/setting-product-company',
                ];
                filter = filter.filter((item) => !shoppingCart.includes(item.path));
            }
            if (!eventInfo.has_customize) {
                const custom = [
                    '/setting-custom',
                ];
                filter = filter.filter((item) => !custom.includes(item.path));
            }
            if (role === Role.operatorAdmin) {
                const operatorAdmin = [
                    '/company-event-management',
                    '/all-seminar',
                    '/all-live-stream',
                    '/product-payment-management',
                    '/all-seminar',
                    '/setting-booth',
                    '/setting-seminar-room',
                    '/shopping-cart',
                    '/survey-management',
                    '/email-outer-server',
                    '/setting-custom',
                    '/setting-multi-language',
                ];
                filter = filter.filter((item) => !operatorAdmin.includes(item.path));
            }
            if (_.isEmpty(eventInfo.languages)) {
                const multiLang = [
                    '/setting-multi-language',
                ];
                filter = filter.filter((item) => !multiLang.includes(item.path));
            }
            setFilterRoutes(filter);
        }
    }, [eventInfo, routes, routeParams, organizerType, organizerLiveStream, organizerBooth, role]);

    const activeRoute = useCallback((routeName) => match.path.indexOf(routeName) > -1, [match]);

    const handleLogOutSystem = () => {
        if (logOut) {
            logOut();
        }
    };

    const handleEditEventDialog = () => {
        setIsOpenDialogEvent(true);
    };

    const links = (
        <List className={classes.list}>
            {
                params.shortName && role === Role.admin && (
                    <NavLink
                        to={`${'/admin/list-events'}`}
                        className={classes.item}
                        activeClassName="active"
                        exact
                    >
                        <ListItem button className={classes.itemLink}>
                            <DashboardIcon />
                            <ListItemText
                                className={classes.itemText}
                                primary={<FormattedMessage id="adminSidebar.manageXPAND" />}
                                disableTypography
                            />
                        </ListItem>
                    </NavLink>
                )
            }
            {
                role === Role.masterAdmin && (
                    <div>
                        <ListItem button className={classes.itemLink} onClick={handleEditEventDialog}>
                            <DashboardIcon />
                            <ListItemText
                                className={classes.itemText}
                                primary={<FormattedMessage id="adminSidebar.editEventDialog" />}
                                disableTypography
                            />
                        </ListItem>
                    </div>
                )
            }
            {
                isCompanyDetailPage && (role === Role.admin || role === Role.masterAdmin) && (
                    <NavLink
                        to={`${'/admin/'}${params.shortName}${'/company-event-management'}`}
                        className={classes.item}
                        activeClassName="active"
                        exact
                    >
                        <ListItem button className={classes.itemLink}>
                            <Company />
                            <ListItemText
                                className={classes.itemText}
                                primary={<FormattedMessage id="adminSidebar.companyEventManagement" />}
                                disableTypography
                            />
                        </ListItem>
                    </NavLink>
                )
            }
            {filterRoutes.map((prop) => {
                const listItemClasses = classNames({
                    [` ${classes[color]}`]: activeRoute(`${prop.layout ? prop.layout : ''}${prop.param ? prop.param : ''}${prop.path}`),
                });
                const whiteFontClasses = classNames({
                    [` ${classes.whiteFont}`]: activeRoute(`${prop.layout ? prop.layout : ''}${prop.param ? prop.param : ''}${prop.path}`),
                });
                return (
                    <NavLink
                        to={`${prop.layout ? prop.layout : ''}${routeParams ? `/${routeParams}` : ''}${prop.path}`}
                        className={classes.item}
                        activeClassName="active"
                        exact
                        key={prop.path}
                    >
                        <ListItem button className={classes.itemLink + listItemClasses}>
                            <prop.icon />
                            <ListItemText
                                primary={prop.name}
                                className={classNames(classes.itemText, whiteFontClasses)}
                                disableTypography
                            />
                        </ListItem>
                    </NavLink>
                );
            })}
            <div className={classes.logOutButton}>
                <ListItem button className={`${classes.itemLink} ${classes.orange}`} onClick={handleLogOutSystem}>
                    <LogOutIcon />
                    <ListItemText
                        primary={<FormattedMessage id="logout" />}
                        className={classNames(classes.itemText)}
                        disableTypography
                    />
                </ListItem>
            </div>
        </List>
    );

    return (
        <>
            <Hidden mdUp implementation="css">
                <Drawer
                    variant="temporary"
                    anchor="right"
                    open={open}
                    classes={{
                        paper: classNames(classes.drawerPaper),
                    }}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                >
                    <div className={classes.logo}>
                        <Typography
                            variant="h4"
                            color="white"
                            bold
                            onClick={role === Role.masterAdmin ? () => {} : () => history.push('/admin/list-events')}
                            className={role === Role.masterAdmin ? '' : classes.brand}
                        >
                            XPAND
                        </Typography>
                    </div>
                    <div className={classes.sidebarWrapper}>
                        <AdminHeader />
                        {links}
                    </div>
                    <div
                        className={classes.background}
                        style={{ backgroundImage: `url(${image})` }}
                    />
                </Drawer>
            </Hidden>
            <Hidden smDown implementation="css">
                <Drawer
                    anchor="left"
                    variant="permanent"
                    open
                    classes={{
                        paper: classNames(classes.drawerPaper),
                    }}
                >
                    <div className={classes.logo}>
                        <Typography
                            variant="h4"
                            color="white"
                            bold
                            onClick={role === Role.masterAdmin ? () => {} : () => history.push('/admin/list-events')}
                            className={role === Role.masterAdmin ? '' : classes.brand}
                        >
                            XPAND
                        </Typography>
                    </div>
                    <div className={classes.sidebarWrapper}>
                        {links}
                    </div>
                    <div
                        className={classes.background}
                        style={{ backgroundImage: `url(${image})` }}
                    />
                </Drawer>
            </Hidden>

            <EventDialog
                isOpen={isOpenDialogEvent}
                handleClose={() => setIsOpenDialogEvent(false)}
                eventId={eventInfo.id}
                eventShortName={eventInfo.short_name}
            />
            <SnackBar
                open={isOpenSnackBar.isOpen}
                onClose={() => setIsOpenSnackBar({ isOpen: false, color: '', message: '' })}
                message={isOpenSnackBar.message}
                severity={isOpenSnackBar.color}
                autoHideDuration={3000}
            />
        </>
    );
};

SideBar.defaultProps = {
    color: 'blue',
    image: '',
    routes: {},
    open: false,
    handleDrawerToggle: () => {},
    eventInfo: {},
    organizerType: 0,
    organizerLiveStream: false,
    organizerBooth: 4,
    editSuccess: false,
    role: 0,
};

SideBar.propTypes = {
    color: PropTypes.string,
    image: PropTypes.string,
    routes: PropTypes.oneOfType([() => null, PropTypes.array]),
    open: PropTypes.bool,
    handleDrawerToggle: PropTypes.func,
    logOut: PropTypes.func.isRequired,
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    organizerType: PropTypes.number,
    organizerLiveStream: PropTypes.bool,
    fetchGetDetailCompanyForSideBar: PropTypes.func.isRequired,
    organizerBooth: PropTypes.number,
    role: PropTypes.number,
    editSuccess: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    organizerType: state.adminCompanyManagement.detailCompanyForSideBar.data
        && state.adminCompanyManagement.detailCompanyForSideBar.data
        && state.adminCompanyManagement.detailCompanyForSideBar.data.user
        && state.adminCompanyManagement.detailCompanyForSideBar.data.user.organizer_type,
    organizerLiveStream: state.adminCompanyManagement.detailCompanyForSideBar.data
        && state.adminCompanyManagement.detailCompanyForSideBar.data
        && state.adminCompanyManagement.detailCompanyForSideBar.data.user
        && state.adminCompanyManagement.detailCompanyForSideBar.data.user.organizer_live_stream,
    organizerBooth: state.adminCompanyManagement.detailCompanyForSideBar.data
        && state.adminCompanyManagement.detailCompanyForSideBar.data
        && state.adminCompanyManagement.detailCompanyForSideBar.data.user
        && state.adminCompanyManagement.detailCompanyForSideBar.data.user.organizer_booth,
    role: state.auth?.credential?.data?.role,
    editSuccess: state.eventManagement.editEvent.success,
});

const mapDispatchToProps = (dispatch) => ({
    logOut: () => {
        dispatch(LogoutRequested());
    },
    fetchGetDetailCompanyForSideBar: (params) => {
        dispatch(GetDetailCompanyForSidebarRequested(params));
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(SideBar);
